import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useAsyncFn, useDeepCompareEffect } from 'react-use';
import { Link, useHistory } from 'react-router-dom';

import { __t } from '@mssgme/shared';
import { useNavigationUtils } from '@mssgme/hooks';
import { productsApi } from '@mssgme/lib';
import { Spinner } from '@mssgme/ui';
import { ProductItem } from '../../../products';
import { useModalThemes } from '../../hooks';
import { Grid, MoreButton, ThemedModal } from '../../UI';

import styles from './CollectionView.scss';

export const CollectionView = ({ workspaceId, websiteId, collection }) => {
    const history = useHistory();
    const { style } = useModalThemes();
    const { generateUrl } = useNavigationUtils();
    const [{ next, results }, setProducts] = useState({
        next: null,
        results: [],
    });
    const backPath = generateUrl('../..');

    const [{ loading: isFetching }, getProducts] = useAsyncFn(
        async (next, resetStore) => {
            const productsStore = await productsApi.list(workspaceId, {
                next,
                collection: collection._id,
                website: websiteId,
                published: true,
            });

            setProducts((prevProducts) => ({
                ...productsStore,
                results: resetStore ? productsStore.results : prevProducts.results.concat(productsStore.results),
            }));
        },
        [workspaceId, websiteId, collection._id]
    );

    const handleClose = useCallback(() => history.push(backPath), [backPath]);
    const handleMore = useCallback(() => getProducts(next), [next, getProducts]);

    useDeepCompareEffect(() => {
        const initProducts = async () => {
            await getProducts(null, true);
        };

        void initProducts();
    }, [collection, getProducts]);

    return (
        <ThemedModal overlay title={collection.default ? __t('all_products') : collection.name} onClose={handleClose}>
            <div className={styles.content}>
                {results.length > 0 && (
                    <Grid itemClassName={styles.animated}>
                        {results.map((item) => (
                            <ProductItem
                                key={item._id}
                                product={item}
                                tag={Link}
                                to={{
                                    pathname: generateUrl(
                                        `../../c/${collection.slug.toLowerCase()}/p/${item.slug.toLowerCase()}`
                                    ),
                                    state: {
                                        fromCollection: collection._id,
                                    },
                                }}
                                themeStyles={style}
                            />
                        ))}
                    </Grid>
                )}

                {isFetching ? (
                    <Spinner />
                ) : (
                    !!next && (
                        <div className={cn(styles.moreBtnContainer, styles.animated)}>
                            <MoreButton
                                className={styles.moreBtn}
                                style={style}
                                onClick={handleMore}
                                disabled={isFetching}
                            />
                        </div>
                    )
                )}
            </div>
        </ThemedModal>
    );
};

CollectionView.propTypes = {
    workspaceId: PropTypes.string.isRequired,
    websiteId: PropTypes.string,
    isAdmin: PropTypes.bool,
    collection: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        default: PropTypes.bool,
    }).isRequired,
};
